import { formatISO } from "date-fns";
import ChatService from "src/components/pages/home/service/chatService";
import {
  setErrorMessage,
  setInputText,
  setIsInputDisabled,
  setIsLoading,
  setIsRecentChatsLoaded,
  setRecentChats,
  setRecentInput,
  setResponses,
  setShowResult,
} from "src/components/pages/home/store/chatSlice";
import {
  ALIAS,
  BREAKDOWN_TOOL,
  CONVERSATION_ID,
  FAILED_QUESTION_COUNT,
  INJURY_RATE_TOOL,
  IS_EMPTY_DATA,
  IS_TEXT_ONLY,
  NO_MATCHED_TOOL,
  SAIGE_POLICY_TOOL,
  TOOL_NAME,
  TOTAL_SUCCESSFUL_QUESTION_COUNT,
  USER_TEXT,
} from "src/constants/constants";
import {
  GENERIC_ERROR_MESSAGE,
  TIMEOUT_ERROR_MESSAGE,
} from "src/constants/errorMessageConstants";
import { useAppDispatch, useAppSelector } from "src/store/store";
import { sendCustomRUMEvent } from "src/utils/awsRUM";
import { ChatResponse } from "../store/types";

/**
 * ChatHandleSubmit component handles the submission of chat messages
 * @param prompt - The user's input message
 */
const ChatHandleSubmit = (prompt: string) => {
  const dispatch = useAppDispatch();
  const { alias } = useAppSelector((state) => state.user);
  const { conversationId } = useAppSelector((state) => state.chatSlice);

  /**
   * Initializes the state for a new chat submission
   */
  const initializeState = () => {
    dispatch(setShowResult(true));
    dispatch(setIsInputDisabled(true));
    dispatch(setIsLoading(true));
    dispatch(setRecentInput(prompt));
    dispatch(setErrorMessage(""));
  };

  /**
   * Determines the tool name based on the LLM response
   * @param llmResponse - The response from the language model
   * @returns The determined tool name
   */
  const determineToolName = (llmResponse: any): string => {
    if (!llmResponse) return NO_MATCHED_TOOL;

    const { tool_name } = llmResponse;
    if (tool_name === INJURY_RATE_TOOL) return INJURY_RATE_TOOL;
    if (tool_name === BREAKDOWN_TOOL) return BREAKDOWN_TOOL;
    if (tool_name === SAIGE_POLICY_TOOL) return SAIGE_POLICY_TOOL;
    return NO_MATCHED_TOOL;
  };

  /**
   * Checks if the chart data is not empty
   * @param chartJson - The chart data to check
   * @returns boolean indicating if the chart has data
   */
  const checkChartNotEmpty = (chartJson: any): boolean => {
    return (
      chartJson !== null && chartJson !== undefined && chartJson.data.length > 0
    );
  };

  /**
   * Creates the response data object from the server response
   * @param data - The server response data
   * @returns Formatted response data
   */
  const createResponseData = (data): ChatResponse => {
    return {
      messageId: data.message_id,
      userText: prompt,
      isSuccessful: data.successful,
      llmResponse: data.llm_response_json,
      chartResult: JSON.stringify(data.chart_json),
      thumbsUpClicked: false,
      thumbsDownClicked: false,
      voteResponse: "",
      isTextOnly: data.isTextOnly,
      llmMessage: data.llm_message,
      isEmptyData: data.is_empty_data,
      confirmationResponse: "",
      confirmationYesClicked: false,
      confirmationNoClicked: false,
      datetime: formatISO(new Date()),
      numericalAnswer: data.numerical_answer,
      showCsv: data.show_csv,
      csvData: data.csv_data,
      csvPreview: data.csv_preview,
      relatedDocuments: data.related_documents,
      sourceAttributions: data.source_attributions,
    };
  };

  /**
   * Handles successful chat submission
   * @param data - The server response data
   */
  const handleSuccess = async (data) => {
    // Extract and compute necessary flags
    const isTextOnly = data.successful && data.isTextOnly;
    const toolName = determineToolName(data.llm_response_json);

    // Create and dispatch response data
    const responseData = createResponseData(data);
    dispatch(setResponses(responseData));

    // Handle text-only responses
    if (isTextOnly) {
      dispatch(setIsInputDisabled(false));
    }

    // Reset loading state and input
    dispatch(setIsLoading(false));
    dispatch(setInputText(""));

    // Fetch and update recent chats
    const recentChats = await ChatService.getRecentChats();
    dispatch(setRecentChats(recentChats.data.recent_messages));
    dispatch(setIsRecentChatsLoaded(true));

    // Send analytics event
    sendCustomRUMEvent(TOTAL_SUCCESSFUL_QUESTION_COUNT, {
      [ALIAS]: alias,
      [USER_TEXT]: prompt,
      [CONVERSATION_ID]: conversationId,
      [IS_TEXT_ONLY]: isTextOnly,
      [IS_EMPTY_DATA]: responseData.isEmptyData,
      [TOOL_NAME]: toolName,
    });
  };

  /**
   * Handles errors during chat submission
   * @param error - The error object
   */
  const handleError = (error) => {
    // Check if it's an API Gateway timeout error
    const isApiGatewayTimeout =
      error?.response?.status === 504 ||
      error?.status === 504 ||
      error?.message?.includes("status code 504");

    const errorMessage = isApiGatewayTimeout
      ? TIMEOUT_ERROR_MESSAGE
      : GENERIC_ERROR_MESSAGE;

    // Reset state and show error message
    dispatch(setErrorMessage(errorMessage));
    dispatch(setIsInputDisabled(false));
    dispatch(setIsLoading(false));
    dispatch(setInputText(""));

    // Send error analytics
    sendCustomRUMEvent(FAILED_QUESTION_COUNT, {
      [ALIAS]: alias,
      [USER_TEXT]: prompt,
      [CONVERSATION_ID]: conversationId,
      is_timeout_error: isApiGatewayTimeout,
    });
  };

  /**
   * Main function that handles the chat submission process
   */
  return async () => {
    try {
      initializeState();

      // Send chat request to server
      const response = await ChatService.postChart({
        query: prompt,
        conversation_id: conversationId,
      });

      await handleSuccess(response.data);
    } catch (error) {
      handleError(error);
    }
  };
};

export default ChatHandleSubmit;
